// packages/core/src/learning/school-modules/technical-trading.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'technical-trading' school. LearningModule,
// surface 'school'.
//   module id   = 'school-technical-trading' (matches exhibit nativeModuleId)
//   homeSchool  = 'technical-trading'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_TECHNICAL_TRADING_MODULE: LearningModule = {
	id: 'school-technical-trading',
	title: 'Technical Trading',
	description:
		'The lens that reads price as crowd pressure — charts as behavior not prophecy, process over setup, trend as trap, risk control as the real craft, and where it turns into gambling.',
	emoji: '📊',
	accentColor: '#e09b1f',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'technical-trading',
	lessons: [
		{
			id: 'school-technical-trading-01',
			title: 'Charts show pressure, not certainty',
			description:
				'This lesson teaches how technical trading sees price charts as behavior maps, not predictions.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['technical-trading'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['technical-trading', 'price-pressure', 'chart-uncertainty'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['price pressure', 'behavior map', 'chart uncertainty'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A chart can make the market look cleaner than it really is. Lines, candles, and patterns can create the feeling that the future is visible.',
				},
				{
					type: 'paragraph',
					text: 'Technical trading is paying attention to price behavior. This school sees charts as a record of buyers, sellers, fear, greed, attention, and pressure moving through the market.',
				},
				{
					type: 'bullet_list',
					items: [
						'Price shows behavior',
						'Volume can show interest',
						'Patterns can fail',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating a chart like a prediction instead of a clue.',
				},
				{
					type: 'example',
					text: 'A stock may break above a prior high and look strong. A trader may see pressure building. But the move can still fail if buyers disappear, news changes, or the breakout attracts late entries that quickly panic.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is uncertainty. A chart can help frame a decision, but it cannot make the outcome reliable. Losses are possible, and speculation is not dependable wealth.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens starts with interpretation, not action. The point is to understand what technical traders notice before confusing a setup with a promise.',
				},
				{
					type: 'key_takeaway',
					text: 'A chart shows pressure. It does not show the future.',
				},
			],
			audioScript:
				'Technical trading starts with a different way of reading the market. Instead of beginning with a company’s earnings, balance sheet, or long term business story, this lens begins with price behavior. It asks, “What are buyers and sellers doing right now? Where is pressure building? Where did price react before? Where does fear or excitement seem to be showing up?”\n\nThat does not mean the chart knows the future. This is the first thing to get right. A chart is not a prophecy. It is a record. It records transactions, movement, volume, and reaction. Technical traders study that record because they believe price behavior can reveal something about crowd psychology and market pressure.\n\nThink about a crowded doorway. You may not know every person’s reason for moving, but you can still see where pressure is building. You can see whether people are pushing in one direction, whether the crowd is slowing down, or whether a bottleneck is forming. Technical trading tries to do something similar with price. It does not always care why people are buying or selling first. It cares that buying or selling pressure is showing up.\n\nBut this is exactly where beginners get in trouble. They see a pattern and treat it like a prediction. They think a breakout must continue, a support area must hold, or a trend line must matter. The chart begins to feel like a signal from the future. That is dangerous.\n\nA setup can look clean and still fail. Price can break above a prior level, pull in excited buyers, then reverse hard. A support area can hold three times and fail the fourth. A trend can look strong until one new piece of information changes the mood. Volume can look meaningful, then disappear when liquidity dries up. The chart can give structure, but it cannot guarantee the next move.\n\nThis is why technical trading is speculation. Losses are possible. Outcomes are not reliable. A person can read a chart carefully and still be wrong. The mature version of the lens does not pretend otherwise. It uses charts to frame risk, not to remove risk.\n\nInside Nisk, this lesson matters because people often want certainty before making money decisions. Technical trading can feed that desire if it is taught badly. It gives people lines, labels, and patterns that feel official. But the real lesson is humility. A chart may help you ask better questions, such as where pressure has appeared, where a trade idea breaks, or where emotion might be entering the decision.\n\nA healthier technical trading mindset sounds like this: “This chart suggests pressure may be shifting, but I do not know the future. Here is what would make the idea wrong. Here is the risk. Here is the exit. Here is the size that would keep the loss survivable.”\n\nThat is very different from saying, “The chart says this will happen.”\n\nThe takeaway is simple. Charts show behavior and pressure. They do not show certainty. The moment you treat a pattern like a promise, the lens has already started to break.',
			flashcards: [
				{
					id: 'school-technical-trading-01-fc-1',
					role: 'main_idea',
					front: 'What does a chart show?',
					back: 'It shows price behavior, pressure, and crowd reaction.',
				},
				{
					id: 'school-technical-trading-01-fc-2',
					role: 'watch_out',
					front: 'What is the chart trap?',
					back: 'Treating a pattern like a prediction.',
				},
				{
					id: 'school-technical-trading-01-fc-3',
					role: 'use_it',
					front: 'How should charts be used?',
					back: 'Use them to frame risk, not to claim certainty.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Read the pressure',
				prompt: 'Write what a chart might suggest, then write why that suggestion could still fail.',
			},
			revision: 1,
		},
		{
			id: 'school-technical-trading-02',
			title: 'The setup is not the edge',
			description:
				'This lesson teaches why technical trading depends on risk control and process more than pattern recognition.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['technical-trading'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['technical-trading', 'risk-control', 'process-edge'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['setup discipline', 'risk control', 'process edge'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Beginners often hunt for the perfect setup. Technical trading warns that the setup is only one piece of the game.',
				},
				{
					type: 'paragraph',
					text: 'This school believes a setup matters only when it is paired with risk control, sizing, exit rules, and emotional discipline. A pattern without a plan is just a story with candles.',
				},
				{
					type: 'bullet_list',
					items: [
						'Entry is not enough',
						'Exit rules matter',
						'Size changes everything',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is obsessing over the entry while ignoring the loss path.',
				},
				{
					type: 'example',
					text: 'Two traders can take the same chart pattern. One risks a small amount with a clear exit. The other risks too much and freezes when the trade moves against them. Same setup, different outcome.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is thinking skill lives in seeing the pattern first. Often the harder skill is managing the trade after the pattern appears.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to writing rules before emotion shows up. A setup should never be more detailed than the risk plan.',
				},
				{
					type: 'key_takeaway',
					text: 'In technical trading, the setup matters less than the process around it.',
				},
			],
			audioScript:
				'Technical trading often gets reduced to patterns. Head and shoulders. Breakouts. Pullbacks. Moving averages. Support. Resistance. Trend lines. People see those words and think the setup is the edge.\n\nBut the mature technical trading lens says the setup is not enough. A setup is only a possible reason to pay attention. It is not a full plan. The real process includes entry, exit, position size, risk per trade, market context, liquidity, time frame, and emotional discipline.\n\nThis is where beginners get hurt. They study entries because entries are exciting. An entry feels like action. It feels like the moment where money starts. But the less exciting questions matter more. Where is the idea wrong? How much can be lost? What happens if the trade moves against you immediately? What happens if it almost works, then reverses? What happens if you are right about direction but wrong about timing? What happens if you freeze?\n\nTwo traders can look at the same chart and take the same setup. One has a written plan. They know where the idea fails. They risk an amount that does not damage their stability. If the trade fails, they exit and move on. The other trader sees the same pattern, risks too much, and starts negotiating with the loss. They move the exit. They hope. They stare at the screen. The setup was the same, but the process was completely different.\n\nThat is the point. In technical trading, the pattern does not protect you from yourself. A clean setup can still become a bad decision if the size is wrong, the exit is unclear, or the trader is emotionally attached.\n\nThis school is useful because it forces the idea of risk control into the center. It says being right every time is not the goal. That is impossible. The goal is to survive being wrong. Losses are part of the environment. A system that cannot handle losses is not a system. It is just confidence waiting to be tested.\n\nBut the blind spot is still serious. Technical traders can start believing their setup has more power than it does. They can backfit patterns. They can see meaning where there is noise. They can call every loss a failure of discipline instead of admitting the setup may not have a real edge. They can confuse repetition with proof.\n\nInside Nisk, the useful habit is simple: write the risk plan first. Before the entry, write the exit. Before the upside, write the loss. Before the setup, write the condition that makes the setup invalid. That one habit separates a trade idea from a chart based impulse.\n\nThis is not personal financial advice. It is worldview education. Technical trading sees price behavior as useful, but it respects that usefulness only when risk is controlled. Without risk control, a setup is not a strategy. It is speculation with decoration.\n\nThe takeaway is direct. The setup matters, but the process around the setup matters more. Technical trading breaks when the chart gets more attention than the loss plan.',
			flashcards: [
				{
					id: 'school-technical-trading-02-fc-1',
					role: 'main_idea',
					front: 'Is the setup enough?',
					back: 'No. It needs risk control, sizing, exits, and process.',
				},
				{
					id: 'school-technical-trading-02-fc-2',
					role: 'watch_out',
					front: 'What do beginners overvalue?',
					back: 'The entry, while ignoring the loss path.',
				},
				{
					id: 'school-technical-trading-02-fc-3',
					role: 'use_it',
					front: 'What should come first?',
					back: 'Write the exit and risk before the entry.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write the risk plan',
				prompt: 'Write the exit rule before writing the entry reason.',
			},
			revision: 1,
		},
		{
			id: 'school-technical-trading-03',
			title: 'Trend can turn into trap',
			description:
				'This lesson teaches how technical trading respects trend while admitting that trend following can fail hard.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['technical-trading'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['technical-trading', 'trend-behavior', 'reversal-risk'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['trend behavior', 'reversal risk', 'late entry'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Trends are seductive because they make the market look obvious after the move has already happened.',
				},
				{
					type: 'paragraph',
					text: 'Technical trading pays attention to trend because persistent movement can show strong demand, fear, momentum, or crowd agreement. But trend is not certainty. It is behavior that can change.',
				},
				{
					type: 'bullet_list',
					items: [
						'Trends can persist',
						'Reversals can be violent',
						'Late entries carry risk',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is entering late because the chart finally feels safe.',
				},
				{
					type: 'example',
					text: 'A stock climbs for weeks and looks unstoppable. By the time a beginner feels confident, many earlier buyers may already be ready to sell. The trend can continue, but the late entry carries a different risk.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is timing pressure. Technical trading can make people chase because waiting feels like missing the move.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to separating observation from impulse. Seeing a trend is not the same as needing to act on it.',
				},
				{
					type: 'key_takeaway',
					text: 'Trend is information, not permission to chase.',
				},
			],
			audioScript:
				'Technical trading pays a lot of attention to trend. A trend is not just a line moving up or down. It can be a sign that pressure is persistent. Buyers keep showing up. Sellers keep stepping back. Attention keeps building. Fear keeps spreading. Momentum keeps feeding itself.\n\nThat is why trend matters to this worldview. Technical traders often believe price movement can continue longer than people expect because behavior can reinforce itself. When price rises, it attracts attention. When attention grows, more people watch. When more people watch, some enter. That can push price further, which creates more attention. The loop can continue for a while.\n\nBut the danger is that trends look safest after they have already moved a lot. This is where beginners often get trapped. Early in a trend, the move feels uncertain. Later, after the chart looks obvious, confidence arrives. But by then, the risk may be different. Earlier buyers may already have profit. The price may be extended. Expectations may be crowded. One bad piece of news, one shift in liquidity, or one failed breakout can reverse the mood fast.\n\nA trend can be real and still be a bad entry. That is an important distinction. Technical trading is not just asking whether something is going up. It is asking where the idea is invalid, whether the risk is controlled, whether the entry is late, and whether the trade still offers a reasonable relationship between possible loss and possible reward.\n\nThink about a stock that rises for six straight weeks. Social media starts talking about it. The chart looks clean. Every dip gets bought. A beginner finally decides it is safe because the trend is obvious. But that feeling of safety may be a warning. If everyone now sees the same thing, the easy part may already be gone. The trend can still continue, but the late buyer is entering with different pressure than the early buyer.\n\nThis is why trend can turn into trap. The chart that creates confidence can also create crowding. People chase because they do not want to miss the move. They enter without a clear exit. They size too large because the move looks obvious. Then a normal pullback feels unbearable.\n\nLosses are possible. Outcomes are not reliable. A trend does not owe anyone continuation. Technical trading is speculation when the person is relying on movement continuing without a plan for reversal.\n\nInside Nisk, this lesson connects to separating observation from impulse. You can notice a trend without acting on it. You can say, “There is strength here,” without saying, “I need to enter now.” That space matters. It protects you from turning every observation into a decision.\n\nThe mature version of this lens sounds like this: “The trend is real right now. Here is where it would look broken. Here is the risk. Here is why a late entry may be dangerous. Here is what I would do if the move reverses.”\n\nThe takeaway is simple. Trend is information. It is not permission to chase. A good technical trader respects momentum, but does not worship it.',
			flashcards: [
				{
					id: 'school-technical-trading-03-fc-1',
					role: 'main_idea',
					front: 'Why do trends matter?',
					back: 'They can show persistent pressure, attention, and crowd behavior.',
				},
				{
					id: 'school-technical-trading-03-fc-2',
					role: 'watch_out',
					front: 'What is the late entry trap?',
					back: 'Entering after the chart feels safe because the move is already obvious.',
				},
				{
					id: 'school-technical-trading-03-fc-3',
					role: 'use_it',
					front: 'How do you avoid chasing?',
					back: 'Separate noticing a trend from needing to act on it.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Separate trend from impulse',
				prompt: 'Write one trend you notice, then write why noticing it does not require action.',
			},
			revision: 1,
		},
		{
			id: 'school-technical-trading-04',
			title: 'Risk control is the real discipline',
			description:
				'This lesson teaches why technical trading survives through sizing, exits, journaling, and emotional control.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['technical-trading'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 5,
			tags: ['technical-trading', 'position-sizing', 'emotional-control'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'position sizing',
				'trading journal',
				'emotional control',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Technical trading gets dangerous when the goal becomes being right. The better goal is staying alive long enough to keep thinking clearly.',
				},
				{
					type: 'paragraph',
					text: 'This school believes risk control is the center of the craft. Position size, exit rules, journaling, and emotional review matter because losses are expected.',
				},
				{
					type: 'bullet_list',
					items: [
						'Size controls damage',
						'Exits reduce hesitation',
						'Journals expose patterns',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is learning more indicators instead of fixing behavior.',
				},
				{
					type: 'example',
					text: 'A trader loses money three times in a row. Without risk control, they may size up to win it back. With risk control, the losses are survivable and the journal shows whether the process broke.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is turning risk control language into decoration. Rules only matter if they are followed when the trade feels uncomfortable.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to real life stability. If a speculative activity threatens runway, focus, bills, or mental health, the risk is bigger than the account number.',
				},
				{
					type: 'key_takeaway',
					text: 'Technical trading is mostly risk control under emotional pressure.',
				},
			],
			audioScript:
				'Technical trading is often sold as a game of finding better signals. More indicators. Better patterns. Cleaner setups. Faster alerts. But the serious version of the lens keeps coming back to risk control.\n\nRisk control is the real discipline because losses are expected. A trader who cannot handle losses has no process. They only have hope. Technical trading does not become safer because someone adds more lines to a chart. It becomes more controlled when the person knows how much they can lose, where the idea fails, and how they will behave when the trade becomes uncomfortable.\n\nPosition sizing matters because size controls damage. A small loss can be a normal part of a process. An oversized loss can become a life problem. It can affect rent, debt payoff, sleep, focus, confidence, and decision quality. This is why the real risk is not only the market. The real risk is what the market does to the person making decisions.\n\nExit rules matter because discomfort creates negotiation. When a trade moves against someone, the mind starts bargaining. Maybe it will come back. Maybe the stop was too tight. Maybe the setup is still valid. Maybe the market is wrong. Sometimes patience is reasonable, but often the person is just avoiding the pain of being wrong. A written exit rule helps reduce that debate.\n\nJournaling matters because memory is not honest under pressure. People remember the big win. They forget the sloppy entries. They blame bad luck for losses and call good luck skill. A trading journal can show patterns the ego does not want to see. Maybe losses come after boredom. Maybe size increases after a win. Maybe rules are broken after three losing trades. Maybe the person trades worse when stressed from work or money pressure.\n\nThis is where technical trading becomes more about behavior than charts. A trader can have a decent setup and still fail because of emotion. They can revenge trade. They can chase. They can refuse to exit. They can take trades when tired. They can keep trading because the screen gives constant feedback. That feedback can become addictive.\n\nLosses are possible, outcomes are not reliable, and speculation is not dependable wealth. Risk control does not remove that. It only attempts to keep the damage within boundaries. That is an important difference. The presence of rules does not make trading safe. It makes the risk more visible if the rules are real.\n\nInside Nisk, this connects directly to your real life. If a speculative activity threatens your runway, bills, focus, relationships, or mental health, then the risk is bigger than a chart. A setup that damages your life outside the screen is not small.\n\nA mature technical trading lens asks: What is the maximum damage? What rule prevents one mistake from becoming five mistakes? What does my journal say about my actual behavior? What would make me stop for the day? What would make me stop completely?\n\nThe takeaway is simple. Technical trading is mostly risk control under emotional pressure. The chart is only one part. The person reading the chart is the bigger risk.',
			flashcards: [
				{
					id: 'school-technical-trading-04-fc-1',
					role: 'main_idea',
					front: 'What is the real discipline?',
					back: 'Risk control through size, exits, journaling, and emotional discipline.',
				},
				{
					id: 'school-technical-trading-04-fc-2',
					role: 'watch_out',
					front: 'What does more indicators not fix?',
					back: 'Poor behavior, oversized risk, hesitation, and revenge trading.',
				},
				{
					id: 'school-technical-trading-04-fc-3',
					role: 'use_it',
					front: 'What should a journal reveal?',
					back: 'Whether your behavior matches your rules under pressure.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-technical-trading-05',
			title: 'Where technical trading breaks',
			description:
				'This lesson teaches how technical trading can turn into overconfidence, gambling behavior, and false pattern certainty.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['technical-trading'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 5,
			tags: [
				'technical-trading',
				'gambling-loop',
				'speculation-humility',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'pattern overconfidence',
				'gambling loop',
				'speculation humility',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Technical trading breaks when the chart becomes a permission slip for impulse.',
				},
				{
					type: 'paragraph',
					text: 'This school is useful because it notices price behavior, timing, pressure, and risk control. But it can go wrong when patterns become certainty, losses become revenge, and trading becomes emotional escape.',
				},
				{
					type: 'bullet_list',
					items: [
						'Patterns can be random',
						'Luck can feel like skill',
						'Screens can feed compulsion',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking a system is working because a few trades worked.',
				},
				{
					type: 'example',
					text: 'Someone wins several trades and starts increasing size. Then one bad loss triggers a desperate attempt to win it back. The problem is no longer the chart. The problem is the behavioral loop.',
				},
				{
					type: 'paragraph',
					text: 'This is speculation. Losses are possible, outcomes are not reliable, and frequent feedback can become addictive.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with humility. If trading makes your life smaller, more stressed, or less stable, the chart is not the main issue.',
				},
				{
					type: 'key_takeaway',
					text: 'Technical trading is strongest when the trader respects uncertainty more than the setup.',
				},
			],
			audioScript:
				'Technical trading breaks when the chart becomes a permission slip for impulse.\n\nThat is the hard truth. This school can teach useful things. It can help a person notice price behavior, trend, pressure, timing, and crowd psychology. It can show why risk control matters. It can teach that decisions need exits and sizing, not just opinions. But it can also become a dangerous environment for overconfidence and compulsive behavior.\n\nThe reason is feedback. Trading gives fast feedback. Price moves. The screen updates. A trade turns green or red. The mind reacts. That speed can feel exciting. It can also train bad habits. A win can feel like proof of skill. A loss can feel like something to fix immediately. The person starts chasing the next setup, not because the process is strong, but because the feeling is loud.\n\nThis is where luck can disguise itself as ability. Someone wins a few trades and starts believing they have found something. They increase size. They take more trades. They loosen their rules. They stop asking whether the setup has a real edge. They focus on the recent result because the recent result feels good.\n\nThen a loss hits. If the person has no emotional guardrails, the loss becomes personal. They want to win it back. They take another trade too quickly. Then another. They move from technical trading into revenge trading. At that point, the chart is not the real driver anymore. Emotion is.\n\nPatterns can also create false certainty. Humans are pattern seeking machines. We can see structure in noise. A chart can look meaningful because the mind wants it to be meaningful. A person can draw lines after the fact and convince themselves the answer was obvious. They can keep finding examples that support the pattern while ignoring the setups that failed.\n\nThis is why technical trading must be taught as speculation. Losses are possible. Outcomes are not reliable. Frequent feedback can become addictive. A setup can fail. A rule can be ignored. A person can be right for the wrong reason and wrong even when the chart looked clean.\n\nInside Nisk, the point is not to shame someone for being curious about trading. Curiosity is fine. The point is to keep the worldview honest. Technical trading is a lens for interpreting price behavior. It is not a dependable wealth machine. It is not a replacement for financial stability. It is not a cure for boredom, stress, debt pressure, or wanting life to change fast.\n\nA mature technical trader asks uncomfortable questions. Am I following a process, or am I chasing a feeling? Am I sizing this so a loss is survivable? Am I journaling honestly? Am I trying to win back money? Does this activity make my life more stable or more chaotic? Would I still respect the process after five losses in a row?\n\nThat last question matters. A system that only feels good during wins is not a system. It is a mood.\n\nThe takeaway is direct. Technical trading is strongest when the trader respects uncertainty more than the setup. If trading makes your life smaller, more stressed, or less stable, the chart is not the main issue.',
			flashcards: [
				{
					id: 'school-technical-trading-05-fc-1',
					role: 'main_idea',
					front: 'Where does technical trading break?',
					back: 'When charts become permission for impulse, overconfidence, or revenge trading.',
				},
				{
					id: 'school-technical-trading-05-fc-2',
					role: 'watch_out',
					front: 'Why can wins mislead?',
					back: 'A few wins can be luck, not proof of a real edge.',
				},
				{
					id: 'school-technical-trading-05-fc-3',
					role: 'use_it',
					front: 'What question protects judgment?',
					back: 'Ask whether the activity makes life more stable or more chaotic.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check the behavior loop',
				prompt: 'Write whether trading curiosity would make your life calmer, riskier, more focused, or more reactive.',
			},
			revision: 1,
		},
	],
};
